/**
 * Debounces a fast-changing value (e.g. search text) for API calls or heavy filtering.
 *
 * Where: `Leads` search and other text filters tied to network queries.
 * Uses: `useState` + `useEffect` timer with cleanup.
 * Behavior: updates `delay` ms after the last `value` change; clears pending timeout on unmount.
 */
import { useState, useEffect } from 'react';

export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
